<template>
  <div>
    <h3 class="heading-3 weight-medium pb-10 pb-md-16 text-left text-md-center">
      Who is the child's legal representative?
    </h3>

    <div v-if="adultsExist">
      <div class="input-box-holder" v-validate="errors" v-has-value="firstLegalRepresentative.personIndex">
        <div class="validation-output text-left mb-5">
          <ErrorHandler class="error" :id="`V280_${personIndex}`">
            You have not selected any of the options. To continue, please select one option.
          </ErrorHandler>
        </div>
        <div class="block">
          <RadioButton
            v-for="(adultObject, index) in adultsWithIndexes"
            :key="index"
            class="body-3 weight-regular my-5"
            :title="adultObject.adult.name + ' ' + adultObject.adult.surname"
            :name="'firstLegalRepresentative' + personIndex"
            :option="adultObject.index"
            :value="child.firstLegalRepresentative.personIndex"
            @input="setFirstLegalRepresentative"
          />
          <BooleanRadioButton
            class="body-3 weight-regular my-5"
            title='Another Person'
            :name="'firstLegalRepresentative' + personIndex"
            :option="true"
            :checked="firstLegalRepresentative.personIndex === -1"
            :value="firstLegalRepresentative.personIndex === -1"
            @input="setOtherFirstLegalRepresentative"
          />
        </div>
      </div>

      <h3 class="heading-3 weight-medium pb-10 pb-md-16 text-left text-md-center">
        What is the relationship of the chosen legal representative to the child?
      </h3>

      <div class="input-box-holder" v-validate="errors" v-has-value="firstLegalRepresentative.relationship">
        <div class="validation-output text-left mb-5">
          <ErrorHandler class="error" :id="`V147_${personIndex}`">
            You have not selected any of the options. To continue, please select one option.
          </ErrorHandler>
        </div>

        <div class="block">
          <RadioButton
            v-for="option in relationshipType"
            :title="option.label"
            :key="option.name$"
            class="body-3 weight-regular my-5"
            :option="option"
            :name="'relationshipTypeGroup2' + personIndex"
            v-model="firstLegalRepresentative.relationship"
          />
        </div>
      </div>

      <div v-if="showSecondLegalRepresentativeSection">
        <h3 class="heading-3 weight-medium pb-10 pb-md-16 text-left text-md-center">
          Do you wish to also indicate another legal representative for the child?
        </h3>

        <div class="input-box-holder" v-validate="errors"
             v-has-value="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative">
          <DoubleInlineRadioButton
            :name="'iAmInterestedEnterSecondLegalRepresentativeGroup' + personIndex"
            second-title='No'
            first-title='Yes'
            v-model="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative"/>
          <div class="validation-output text-left mt-3">
            <ErrorHandler class="error" :id="`V296_${personIndex}`">
              Select one of the options.
            </ErrorHandler>
          </div>
        </div>

        <SecondLegalRepresentative
          v-if="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative"
          :model="model"
          :index="personIndex"
          :errors="errors"
          :legal-representative="secondLegalRepresentative"
          :insured-person="child"
        />
        <div
          v-else-if="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative === false">
          <div class="flex justify-md-center align-center pt-16 pb-10">
            <h4 class="weight-medium text-left pr-6">
              Is the other legal representative listed on the child's birth certificate?
            </h4>
          </div>
          <div class="input-box-holder" v-validate="errors"
               v-has-value="secondLegalRepresentative.isSecondLegalRepresentativeInBirthList">
            <DoubleInlineRadioButton
              :name="'isSecondLegalRepresentativeInBirthListGroup' + personIndex"
              second-title='No'
              first-title='Yes'
              v-model="secondLegalRepresentative.isSecondLegalRepresentativeInBirthList"/>
            <div class="validation-output text-left mt-3">
              <ErrorHandler class="error" :id="`V297_${personIndex}`">
                Select one of the options.
              </ErrorHandler>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="input-box-holder" v-validate="errors" v-has-value="firstLegalRepresentative.relationship">
      <div class="validation-output text-left mb-5">
        <ErrorHandler class="error" :id="`V147_${personIndex}`">
          You have not selected any of the options. To continue, please select one option.
        </ErrorHandler>
      </div>

      <RadioButton v-for="option in relationshipType"
                   class="input-box rd-card mb-10 body-1"
                   :title="option.label"
                   :key="option.name$"
                   :value="firstLegalRepresentative.relationship"
                   :option="option"
                   :name="'relationshipTypeGroup' + personIndex"
                   :checked="isSelected(option)"
                   @input="setRelationship"
      >
      </RadioButton>
    </div>

  </div>
</template>
<script>
import RadioButton from '@/components/RadioButton/RadioButton.vue';
import distributionChannels from '@/mixins/distributionChannels';
import routes from '@/mixins/routes';
import GA from '@/mixins/GA';
import ErrorHandler from '@/components/ErrorHandler/ErrorHandler.vue';
import BooleanRadioButton from '@/components/BooleanRadioButton/RadioButton.vue';
import DoubleInlineRadioButton from '@/components/DoubleInlineRadioButton/DoubleInlineRadioButton.vue';
import SecondLegalRepresentative from '@/pagesComponents/SecondLegalRepresentative.vue';
import persons from '@/mixins/persons';

export default {
  components: {
    SecondLegalRepresentative,
    DoubleInlineRadioButton,
    BooleanRadioButton,
    ErrorHandler,
    RadioButton,
  },
  mixins: [distributionChannels, routes, GA, persons],
  props: {
    model: {
      type: Object,
      required: true,
    },
    errors: {
      type: Array,
      required: true,
    },
    firstLegalRepresentative: {
      type: Object,
      required: true,
    },
    secondLegalRepresentative: {
      type: Object,
      required: true,
    },
    child: {
      type: Object,
      required: true,
    },
    personIndex: {
      type: Number,
      required: true,
    },
    adultsExist: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    relationshipType() {
      const { RelationshipEnum: options } = this.enumsValues;
      const { RelationshipEnum: enumeration } = this.enums;
      return options.filter((option) => {
        if (this.adultsExist) {
          if (this.firstLegalRepresentative.personIndex === -1) return true;
          return option !== enumeration.PO;
        }
        return true;
      });
    },
    insuranceReasonType() {
      const { InsuredPersonTypeEnum: enumeration } = this.enums;
      return enumeration;
    },
    showSecondLegalRepresentativeSection() {
      return this.child.insuredPersonType !== this.insuranceReasonType.CHANGE_OF_INSURANCE_COMPANY_CHILD
        && this.firstLegalRepresentative.personIndex !== -1
        && this.firstLegalRepresentative.personIndex !== null;
    },
  },
  methods: {
    isSelected(option) {
      return this.firstLegalRepresentative.relationship === option;
    },
    setRelationship(relationship) {
      // this.infoLegalPerson = true;
      this.firstLegalRepresentative.relationship = relationship;
      if (relationship === this.enums.RelationshipEnum.PO) {
        this.firstLegalRepresentative.withoutAddress = false;
      }
      this.firstLegalRepresentative.personIndex = -1;
    },
    setOtherFirstLegalRepresentative() {
      this.$emit('setOtherFirstLegalRepresentative', this.child);
    },
    setFirstLegalRepresentative(adultIndex) {
      this.$emit('setFirstLegalRepresentative', this.child, adultIndex);
    },
  },
};
</script>
